import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import DrawerCDS, {
  DrawerSize,
} from 'app/shared/componentsRestyled/coreDesignSystem/Drawer/DrawerCDS';
import TextFieldCDS from 'app/shared/componentsRestyled/coreDesignSystem/form/TextFieldCDS';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import { LabelVariant } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import StatusIconCDS from 'app/shared/componentsRestyled/coreDesignSystem/StatusIconCDS';
import PropTypes from 'prop-types';

import { StatusEnum } from 'constants/status';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  getAusCasefilePrefillAction,
  startWholesaleAusSubmitAction,
} from 'store/loanApplications/actions';
import { selectAusCasefileIntake, selectAusMismoPrefill } from 'store/loanApplications/selectors';

import {
  CASEFILE_INTAKE_FORM_ID,
  DRAWER_TITLE,
  DU_CASEFILE_FIELD_NAME,
  DU_CASEFILE_ID_LENGTH,
  DU_ERROR_MESSAGE,
  DU_FIELD_LABEL,
  DU_FIELD_PLACEHOLDER,
  FOOTNOTE_MESSAGE,
  INFO_MESSAGE,
  LP_AUS_KEY_FIELD_NAME,
  LP_FIELD_LABEL,
  LP_FIELD_PLACEHOLDER,
  PREFILL_LABEL,
  PREFILL_NOTE,
  SUBMIT_BUTTON_LABEL,
} from './AusCasefileIntakeDrawer.constants';
import {
  formStackStyles,
  infoBoxStyles,
  infoIconColumnStyles,
  prefillNoteStyles,
} from './AusCasefileIntakeDrawer.styles';
import { AusCasefileIntakeDrawerTestIds } from './AusCasefileIntakeDrawer.testIds';
import { isCasefileIntakeSubmittable, isDuCasefileIdValid } from './AusCasefileIntakeDrawer.utils';

const NUMERIC_INPUT_PROPS = { inputMode: 'numeric' };

const AusCasefileIntakeDrawer = ({ isOpen, onClose, companyId, loanId }) => {
  const dispatch = useDispatch();

  const prefill = useSelector(selectAusCasefileIntake);
  const mismoPrefill = useSelector(selectAusMismoPrefill);

  const [duCasefileId, setDuCasefileId] = useState('');
  const [lpAusKey, setLpAusKey] = useState('');
  const [isDuTouched, setIsDuTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrefillApplied, setIsPrefillApplied] = useState(false);

  useEffectSafeStrictMode(() => {
    if (isOpen && loanId) {
      dispatch(getAusCasefilePrefillAction({ companyId, loanId }));
    }
  }, [dispatch, isOpen, companyId, loanId]);

  // Seed the fields from the MISMO-imported casefile the first time it lands while open,
  // without clobbering anything the user has since typed.
  useEffect(() => {
    if (isOpen && !isPrefillApplied && (prefill.duCasefileId || prefill.lpAusKey)) {
      setDuCasefileId(prefill.duCasefileId ?? '');
      setLpAusKey(prefill.lpAusKey ?? '');
      setIsPrefillApplied(true);
    }
  }, [isOpen, isPrefillApplied, prefill.duCasefileId, prefill.lpAusKey]);

  const trimmedDu = duCasefileId.trim();
  const trimmedLp = lpAusKey.trim();

  const hasDuError = isDuTouched && !isDuCasefileIdValid(duCasefileId);
  const isSubmitDisabled = isSubmitting || !isCasefileIntakeSubmittable({ duCasefileId, lpAusKey });
  const isDirty = !!(trimmedDu || trimmedLp);
  const isDuPrefilled = !!mismoPrefill.duCasefileId && mismoPrefill.duCasefileId === trimmedDu;
  const isLpPrefilled = !!mismoPrefill.lpAusKey && mismoPrefill.lpAusKey === trimmedLp;

  const resetForm = useCallback(() => {
    setDuCasefileId('');
    setLpAusKey('');
    setIsDuTouched(false);
    setIsPrefillApplied(false);
  }, []);

  const onDuChange = useCallback(event => {
    setDuCasefileId(event.target.value);
    setIsDuTouched(true);
  }, []);
  const onLpChange = useCallback(event => setLpAusKey(event.target.value), []);

  const onDrawerClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const onSubmit = useCallback(
    event => {
      event.preventDefault();

      if (isSubmitDisabled) {
        return;
      }

      setIsSubmitting(true);

      dispatch(
        startWholesaleAusSubmitAction({
          companyId,
          loanId,
          duCasefileId: trimmedDu || null,
          lpAusKey: trimmedLp || null,
        }),
      )
        .then(() => {
          resetForm();
          onClose();
        })
        // catchAsyncActionError shows the error toast then re-rejects; swallow the expected
        // rejection so a failed retrieve keeps the drawer open without an unhandled rejection.
        .catch(Error)
        .finally(() => setIsSubmitting(false));
    },
    [isSubmitDisabled, dispatch, companyId, loanId, trimmedDu, trimmedLp, resetForm, onClose],
  );

  const content = (
    <Box
      noValidate
      component="form"
      data-testid={AusCasefileIntakeDrawerTestIds.DRAWER_FORM}
      id={CASEFILE_INTAKE_FORM_ID}
      sx={formStackStyles}
      onSubmit={onSubmit}
    >
      <Box sx={infoBoxStyles}>
        <Box sx={infoIconColumnStyles}>
          <StatusIconCDS size={20} type={StatusEnum.INFO} />
        </Box>
        <Typography variant="regular14">{INFO_MESSAGE}</Typography>
      </Box>

      <TextFieldCDS
        dataTestId={AusCasefileIntakeDrawerTestIds.DU_CASEFILE_FIELD}
        error={hasDuError}
        helperText={hasDuError ? DU_ERROR_MESSAGE : ''}
        inputProps={NUMERIC_INPUT_PROPS}
        label={DU_FIELD_LABEL}
        maxLength={DU_CASEFILE_ID_LENGTH}
        name={DU_CASEFILE_FIELD_NAME}
        placeholder={DU_FIELD_PLACEHOLDER}
        value={duCasefileId}
        onChange={onDuChange}
      />

      {isDuPrefilled && (
        <Box sx={prefillNoteStyles}>
          <LabelCDS size="S" title={PREFILL_LABEL} variant={LabelVariant.BLUE} />
          <Typography color="text.secondary" variant="regular12">
            {PREFILL_NOTE}
          </Typography>
        </Box>
      )}

      <TextFieldCDS
        dataTestId={AusCasefileIntakeDrawerTestIds.LP_AUS_KEY_FIELD}
        label={LP_FIELD_LABEL}
        name={LP_AUS_KEY_FIELD_NAME}
        placeholder={LP_FIELD_PLACEHOLDER}
        value={lpAusKey}
        onChange={onLpChange}
      />

      {isLpPrefilled && (
        <Box sx={prefillNoteStyles}>
          <LabelCDS size="S" title={PREFILL_LABEL} variant={LabelVariant.BLUE} />
          <Typography color="text.secondary" variant="regular12">
            {PREFILL_NOTE}
          </Typography>
        </Box>
      )}

      <Typography color="text.secondary" variant="regular12">
        {FOOTNOTE_MESSAGE}
      </Typography>
    </Box>
  );

  const actions = (
    <Button
      data-testid={AusCasefileIntakeDrawerTestIds.SUBMIT_BUTTON}
      disabled={isSubmitDisabled}
      form={CASEFILE_INTAKE_FORM_ID}
      type="submit"
      variant="contained"
    >
      {SUBMIT_BUTTON_LABEL}
    </Button>
  );

  return (
    <DrawerCDS
      actions={actions}
      content={content}
      dataTestId={AusCasefileIntakeDrawerTestIds.DRAWER_CONTAINER}
      isDirty={isDirty}
      isLoading={isSubmitting}
      open={isOpen}
      size={DrawerSize.SMALL}
      title={DRAWER_TITLE}
      onClose={onDrawerClose}
    />
  );
};

AusCasefileIntakeDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  companyId: PropTypes.string,
  loanId: PropTypes.string,
};

export default AusCasefileIntakeDrawer;
